import React, { useEffect, useState } from "react";
import Layout from "../shared/Layout";
import {
  getGeminiCompletion,
  getGeminiEmbedding,
  getGeminiJson,
} from "../ai/GeminiUtils";
import { languages } from "../tasks/CreateTask";
import logo from "./../../assets/cubes_with_background.png";
import { supabase } from "../../supabaseClient";
import { Task } from "../tasks/models/Task";
import { useAuth } from "../authentication/AuthContext";
import { RGBColor, SketchPicker } from "react-color";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useOutsideAlerter } from "../shared/hooks/useOutsideAlerter";
import { getRandomHexColor } from "./utils/colors";
import { useQuery, useQueryClient } from "react-query";
import RelatedTasks from "../tasks/components/RelatedTasks";
import { addPoints } from "../user/utils/UserUtils";
import { generateSlug } from "../tasks/utils/TaskUtils";
import { useShowTempMessage } from "../shared/hooks/useUtilityContext";
import SnippetMetadata from "./components/SnippetMetadata";
import SnippetActions from "./components/SnippetActions";
import SnippetFrame from "./components/SnippetFrame";
import SnippetConverter from "./components/SnippetConverter";
import ResponsiveText from "../shared/ResponsiveText";
import { logAnalyticsEvent } from "../engagement/Amplitude";
import { v4 as uuidv4 } from "uuid";
import SignUpModal from "../authentication/SignUpModal";
import LogInModal from "../authentication/LogInModal";
import { PiDotsThreeOutlineVerticalDuotone, PiUserBold } from "react-icons/pi";
import { TaskComment } from "../tasks/models/TaskComment";
import { useSnippet } from "./hooks/useSnippet";
import * as htmlToImage from "html-to-image";
import Config from "../shared/Config";
import TaskHelmet from "../tasks/components/TaskHelmet";

export default function NewSnippet() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [snippet, setSnippet] = useState(
    code ?? localStorage.getItem("task_snippet") ?? ""
  );
  const [title, setTitle] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [tags, setTags] = useState<string[]>([]);
  const [screenshotTitle, setScreenshotTitle] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const snippetRef = React.createRef<HTMLTextAreaElement>();
  const colorPickerRef = React.createRef<HTMLDivElement>();
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);
  const [selectedLines, setSelectedLines] = useState<number[]>([]);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [showBackground, setShowBackground] = useState(true);
  const queryClient = useQueryClient();
  const showTempMessage = useShowTempMessage();
  const showTempErrorMessage = useShowTempMessage();
  const [updating, setUpdating] = useState(false);
  const [converter, setConverter] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const [comment, setComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const [hexColor, setHexColor] = useState<string>(
    localStorage.getItem("color") ?? getRandomHexColor()
  );

  const { loadedSnippet, taskLoading, isError } = useSnippet({ id });

  useEffect(() => {
    if (loadedSnippet?.snippet) {
      setSnippet(loadedSnippet?.snippet ?? "");
      setScreenshotTitle(loadedSnippet?.title ?? "");
      setTags(loadedSnippet?.tags ?? []);
      setHexColor(loadedSnippet?.hex ?? getRandomHexColor());
      setSelectedLanguage(loadedSnippet?.language ?? "");
    }
  }, [loadedSnippet?.snippet]);

  const hexToRgb = (hex: string) => {
    const result =
      /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);

    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
          a: result[4] ? parseInt(result[4], 16) / 255 : 1,
        }
      : {
          r: 241,
          g: 113,
          b: 19,
          a: 1,
        };
  };

  const [backgroundColor, setBackgroundColor] = useState<RGBColor>({
    r: hexColor ? hexToRgb(hexColor)!.r : 241,
    g: hexColor ? hexToRgb(hexColor)!.g : 113,
    b: hexColor ? hexToRgb(hexColor)!.b : 19,
    a: hexColor ? hexToRgb(hexColor)!.a : 1,
  });

  useOutsideAlerter({
    ref: colorPickerRef,
    onClickInside: () => {},
    onClickOutside: () => {
      setPickerVisible(false);
    },
  });
  const onTogglePicker = () => setPickerVisible(!pickerVisible);

  const rgbToHex = (r: number, g: number, b: number) => {
    let rHex = r.toString(16);
    let gHex = g.toString(16);
    let bHex = b.toString(16);
    let aHex = Math.round(backgroundColor.a! * 255).toString(16);

    if (rHex.length === 1) rHex = "0" + rHex;
    if (gHex.length === 1) gHex = "0" + gHex;
    if (bHex.length === 1) bHex = "0" + bHex;
    if (aHex.length === 1) aHex = "0" + aHex;

    const hex = "#" + rHex + gHex + bHex + aHex;
    return hex;
  };

  useEffect(() => {
    localStorage.setItem("color", hexColor);
  }, [hexColor]);

  useEffect(() => {
    localStorage.setItem("task_snippet", snippet);
  }, [snippet]);

  useEffect(() => {
    setHexColor(
      rgbToHex(backgroundColor.r, backgroundColor.g, backgroundColor.b)
    );
  }, [backgroundColor]);

  interface SnippetMetadata {
    title: string;
    description: string;
    tags: string;
    language: string;
  }

  const {
    data: comments,
    isLoading: isAnswersLoading,
    isError: isAnswersError,
  } = useQuery(
    ["comment", id],
    async () => {
      const { data, error } = await supabase
        .from("comments")
        .select("*")
        .eq("task_id", id)
        .order("created_at", { ascending: true });

      return data as TaskComment[];
    },
    {
      enabled: !!id,
    }
  );

  async function createImage(): Promise<Blob> {
    var node = document.getElementById("screenshot-container");

    const titleElement = document.getElementById("screenshot-title");
    let originalDisplay = "";

    if (titleElement) {
      if (!screenshotTitle) {
        originalDisplay = titleElement.style.display;
        titleElement.style.display = "none";
      }
    }

    const url = await htmlToImage.toPng(node!, {
      canvasHeight: node!.clientHeight * 3,
      canvasWidth: node!.clientWidth * 3,
    });

    const blob = (await fetch(url)).blob();

    if (titleElement) {
      titleElement.style.display = originalDisplay;
    }

    return blob;
  }

  async function analyzeSnippet(snippet: string) {
    if (user == null) {
      logAnalyticsEvent("Create A Snippet - Not Logged In");
      setIsSignUpOpen(true);
    } else {
      try {
        setLoading(true);

        const contentToSummarize = snippet;

        const jsonMetadata: string =
          await getGeminiJson(`Give me a title, description, tags, and language for this code snippet:
${contentToSummarize}
      
The title should be concise and imperative.
The description should be a few paragraphs long. 
Do not abbreviate the language. 
Use this JSON schema: \{\"type\":\"object\",\"properties\":\{\"title\":\{\"type\":\"string\"\},\"description\":\{\"type\":\"string\"\},\"tags\":\{\"type\":\"string\"\},\"language\":\{\"type\":\"string\"\}\}\}`);

        const metadata: SnippetMetadata = JSON.parse(jsonMetadata);

        const title = screenshotTitle === "" ? metadata.title : screenshotTitle;
        const summary = metadata.description;
        const tags = metadata.tags;

        const embedding = await getGeminiEmbedding(title + " " + summary);

        const snippetImage = await createImage();

        const imageId = uuidv4();

        await supabase.storage
          .from("snippets")
          .upload(`${imageId}.png`, snippetImage, {
            cacheControl: "3600",
            upsert: false,
          });

        const imageUrl = `https://${Config.projectRef}.supabase.co/storage/v1/object/public/snippets/${imageId}.png`;

        /* await supabase.storage
          .from("snippets")
          .getPublicUrl(`${imageId}.png`); */

        const newSnippet: Task = {
          created_at: new Date().toISOString(),
          user_id: user?.id ?? uuidv4(),
          snippet: snippet,
          edit_count: 0,
          vote_total: 0,
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
          title: title.replaceAll("**", ""),
          description: summary,
          tags: tags
            .split(",")
            .map((tag: string) =>
              tag.trim().toLowerCase().replaceAll(" ", "-")
            ),
          embedding,
          hex: hexColor,
          image_id: imageId,
          image_url: imageUrl,
          language:
            selectedLanguage === ""
              ? metadata.language.toLowerCase()
              : selectedLanguage.toLowerCase(),
        };

        const { data, error } = await supabase
          .from("tasks")
          .insert(newSnippet)
          .select("*");

        addPoints({
          points: 1,
          userId: user?.id,
          reason: "Snippet Submitted",
          taskId: data?.[0].id,
        });

        /* setSavedSnippet(data?.[0]);
      setTitle(title);
      setDescription(summary);
      setTags(tags.split(",").map((tag: string) => tag.trim().toLowerCase())); */
        navigate(`/snippet/${data?.[0].id}/${generateSlug(title)}/`, {
          replace: true,
        });
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  }

  function clearSnippet() {
    localStorage.removeItem("color");
    setTitle(undefined);
    setDescription(undefined);
    setTags([]);
    setScreenshotTitle("");
    setSnippet("");
    setSelectedLines([]);
  }

  //https://zpzdxpppgjacuqpljlnu.supabase.co/storage/v1/object/public/snippets/c48e5036-c2dd-4f28-98e6-ebeeb13b10d2.png
  return (
    <Layout center={false}>
      {loadedSnippet && <TaskHelmet task={loadedSnippet} />}
      <div className="flex flex-col md:flex-row items-center mt-16 w-full justify-center gap-4">
        <img
          src={logo}
          alt="logo"
          className="rounded-lg w-8 md:w-10"
        />
        <div className=" font-bold text-3xl text-center">Code on the Rocks</div>
      </div>
      {taskLoading && (
        <div className="w-full flex justify-center p-16">
          <div className="loading loading-dots"></div>
        </div>
      )}
      {!taskLoading && (
        <div className="w-full  flex flex-row justify-center">
          <div
            className={`${
              converter
                ? "px-4 w-full  max-w-screen-2xl items-center flex flex-col"
                : "w-full"
            }`}
          >
            <div className="mx-0 md:mx-4">
              <div className="text-center text-lg">
                <div className="mt-4 px-4">
                  The Bar Is Open And We've Got Snippets on Tap
                </div>
                <div className="text-sm italic">It's good to see you</div>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-2 mt-12 mb-4 w-full">
                <div className="flex flex-row items-center gap-4 justify-start px-4 ">
                  <button
                    className="btn btn-outline btn-accent max-sm:hidden"
                    onClick={() => {
                      logAnalyticsEvent("Toggle Converter", {
                        converter: !converter,
                      });
                      setConverter((prev) => !prev);
                    }}
                  >
                    {converter ? "Frame" : "VS Code Snippet"}
                  </button>
                  {!converter && (
                    <select
                      title="Language"
                      className="select-bordered select border-neutral"
                      value={selectedLanguage}
                      onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                      }}
                    >
                      {languages.map((language) => (
                        <option
                          key={language}
                          value={language}
                        >
                          {language}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {!converter && (
                  <div className="flex flex-row items-center gap-2 justify-start">
                    <div className="relative">
                      <button
                        title="Color Picker"
                        className="btn btn-outline"
                        onClick={onTogglePicker}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setHexColor(getRandomHexColor());
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: hexColor,
                            width: "1.5rem",
                            height: "1.5rem",
                            borderRadius: "0.25rem",
                            border: "1px solid #333",
                          }}
                        ></div>
                      </button>
                      {pickerVisible && (
                        <div>
                          <div className="border-t border-l border-neutral w-4 h-4 bg-neutral absolute rotate-45 top-12 left-4"></div>
                          <div
                            className="absolute top-14 z-50 bg-neutral text-neutral-content"
                            ref={colorPickerRef}
                          >
                            <SketchPicker
                              color={backgroundColor}
                              disableAlpha={false}
                              styles={{
                                default: {
                                  picker: {
                                    backgroundColor: "transparent",
                                  },
                                },
                              }}
                              onChange={(e) => {
                                setBackgroundColor(e.rgb);
                              }}
                              onChangeComplete={(e) => {
                                setBackgroundColor(e.rgb);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex w-full gap-2 items-center">
                      <ResponsiveText
                        longText="Line Numbers"
                        shortText="Line #"
                      />
                      <input
                        title="Show Line Numbers"
                        type="checkbox"
                        className="toggle"
                        checked={showLineNumbers}
                        onChange={(e) => {
                          setShowLineNumbers(e.target.checked);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div
                className={`justify-center flex w-full ${
                  converter ? "h-[800px]" : ""
                }`}
              >
                <div className="flex gap-2 max-w-screen-2xl w-full ">
                  <SnippetFrame
                    backgroundColor={backgroundColor}
                    snippet={snippet}
                    hoveredLine={hoveredLine}
                    selectedLines={selectedLines}
                    setSelectedLines={setSelectedLines}
                    showBackground={showBackground}
                    showLineNumbers={showLineNumbers}
                    snippetRef={snippetRef}
                    setHoveredLine={setHoveredLine}
                    setSnippet={setSnippet}
                    selectedLanguage={selectedLanguage ?? "javascript"}
                    clearSnippet={clearSnippet}
                    loading={loading}
                    hexColor={hexColor}
                    loadedSnippet={loadedSnippet !== undefined}
                    setShowBackground={setShowBackground}
                    setScreenshotTitle={setScreenshotTitle}
                    screenshotTitle={screenshotTitle}
                    converter={converter}
                  />
                  {converter && (
                    <SnippetConverter
                      code={snippet}
                      title={screenshotTitle}
                    />
                  )}
                </div>
              </div>
              <SnippetActions
                analyzeSnippet={analyzeSnippet}
                clearSnippet={clearSnippet}
                code={snippet}
                loading={loading}
                setSnippet={setSnippet}
                tags={tags}
                screenshotTitle={screenshotTitle}
                selectedLanguage={selectedLanguage ?? "javascript"}
                setSelectedLanguage={setSelectedLanguage}
                setUpdating={setUpdating}
                updating={updating}
                id={id}
                key={id}
                snippet={loadedSnippet}
              />
            </div>
            <div className="divider"></div>
            <div className="w-full flex justify-center">
              <div className="max-w-2xl">
                {loading && <div className="mt-4 loading loading-dots"></div>}
                <SnippetMetadata
                  snippet={loadedSnippet}
                  title={title}
                  description={description}
                  tags={tags}
                />
                {loadedSnippet && (
                  <div className="w-full">
                    <div className="text-xl px-4 mt-12">Comments</div>
                    <div className="px-4">
                      <textarea
                        title="Comment"
                        value={comment}
                        placeholder="Post your comment"
                        onChange={(e) => setComment(e.target.value)}
                        className="textarea textarea-bordered w-full px-4 mt-2"
                      ></textarea>
                    </div>
                    <div className="flex justify-end w-full px-4">
                      <button
                        type="submit"
                        className="btn btn-secondary"
                        onClick={() => {
                          if (user == null) {
                            setIsLoginOpen(true);
                            return;
                          }
                          if (comment.trim() === "") {
                            showTempErrorMessage("Comment cannot be empty");
                            return;
                          }
                          setSendingComment(true);
                          setComment("");
                          supabase
                            .from("comments")
                            .insert([
                              {
                                task_id: loadedSnippet!.id,
                                user_id: user?.id,
                                comment: comment,
                                created_at: new Date().toISOString(),
                              },
                            ])
                            .then(() => {
                              setSendingComment(false);
                              queryClient.setQueryData(
                                ["comment", id],
                                [
                                  ...(comments ?? []),
                                  {
                                    task_id: loadedSnippet.id,
                                    user_id: user?.id,
                                    comment: comment,
                                    created_at: new Date().toISOString(),
                                  },
                                ]
                              );
                              queryClient.invalidateQueries(["comment", id]);
                            });
                        }}
                      >
                        Send
                      </button>
                    </div>
                    {comments && (
                      <div className="mt-2 w-full">
                        {comments!.map((comment, index) => {
                          return (
                            <div className="flex items-center gap-4">
                              <div className="grow rounded-md p-4">
                                <div className="">
                                  {comment.comment}
                                  <div className="flex items-center gap-2">
                                    <PiUserBold />
                                    <div className="text-sm">
                                      {new Date(
                                        comment.created_at
                                      ).toLocaleTimeString(undefined, {
                                        hour: "numeric",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {deletingComment && (
                                <div className="loading loading-xs pr-4"></div>
                              )}
                              {user?.id == comment.user_id && (
                                <div className="dropdown dropdown-left">
                                  <div
                                    role="button"
                                    className="cursor-pointer btn"
                                    tabIndex={0}
                                  >
                                    <PiDotsThreeOutlineVerticalDuotone />
                                  </div>
                                  <ul
                                    tabIndex={0}
                                    className="p-2 shadow menu dropdown-content z-[1] bg-base-100 rounded-box w-52"
                                  >
                                    <li>
                                      <a
                                        onClick={async () => {
                                          console.log("delete");
                                          setDeletingComment(true);
                                          try {
                                            await supabase
                                              .from("comments")
                                              .delete()
                                              .eq("id", comment.id);
                                            queryClient.invalidateQueries([
                                              "comment",
                                              id,
                                            ]);
                                            queryClient.setQueryData(
                                              ["comment", id],
                                              comments?.filter(
                                                (c) => c.id !== comment.id
                                              )
                                            );
                                            showTempMessage("Comment deleted");
                                            setDeletingComment(false);
                                          } catch (error) {
                                            setDeletingComment(false);
                                            console.error(
                                              "Error deleting comment:",
                                              error
                                            );
                                            showTempErrorMessage(
                                              "Error deleting comment"
                                            );
                                          }
                                        }}
                                      >
                                        Delete
                                      </a>
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {loadedSnippet && <RelatedTasks task={loadedSnippet!} />}
                <div className="h-16"></div>
              </div>
            </div>
          </div>
        </div>
      )}
      <SignUpModal
        isOpen={isSignUpOpen}
        onClose={() => setIsSignUpOpen(false)}
      />
      <LogInModal
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
      />
    </Layout>
  );
}
